import { PortalRoutes } from "constants/routes";
import history from "features/app/History";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import type { RootState } from "redux/reducers";
import Notifier from "./components/notifier";
import PendingMovementsModal from "./components/pendingMovementsModal";
import { useGetPendingMovementNotifications } from "./hooks/useGetPendingMovementNotifications";
import { useGetSuspendedClientNotifications } from "./hooks/useGetSuspendedClientNotifications";
import { useNotificationCookies } from "./hooks/useNotificationCookies";
import { NotificationItem } from "./notification.types";
import {
  getNotificationsCount,
  isValidPendencyDate,
} from "./utils/notificationUtils";

type StateProps = ReturnType<typeof mapStateToProps>;
export type NotificationProps = StateProps;

const Notification = ({ farmId, clients }: NotificationProps) => {
  const [notificationItems, setNotificationItems] = useState<
    NotificationItem[]
  >([]);
  const [showPendingMovementsModal, setShowPendingMovementsModal] =
    useState<boolean>(false);

  const [
    getPendencyFromCookies,
    setPendencyInCookies,
    getViewedNotificationsFromCookies,
    setViewedNotificationsInCookies,
  ] = useNotificationCookies();

  const toPendingMovementsPage = useCallback(() => {
    history.push(PortalRoutes.PENDING_MOVEMENTS);
  }, []);

  const {
    getPendingMovementsForFarm,
    pendencyOperationDate,
    resetShouldRedirectToPendingMovements,
    setPendencyOperationDate,
    shouldRedirectToPendingMovements,
  } = useGetPendingMovementNotifications({ toPendingMovementsPage });

  const {
    getClientNotifications,
    viewedNotifications,
    setViewedNotifications,
  } = useGetSuspendedClientNotifications({
    clients,
    setViewedNotificationsInCookies,
  });

  useEffect(() => {
    const viewedNotifications = getViewedNotificationsFromCookies();
    setViewedNotifications(viewedNotifications);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const data = getPendencyFromCookies(farmId);
    setPendencyOperationDate(data);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId]);

  useEffect(() => {
    setPendencyInCookies(farmId, pendencyOperationDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, pendencyOperationDate]);

  useEffect(() => {
    if (
      !shouldRedirectToPendingMovements ||
      !isValidPendencyDate(pendencyOperationDate)
    ) {
      return;
    }

    toPendingMovementsPage();
    resetShouldRedirectToPendingMovements();
  }, [
    pendencyOperationDate,
    resetShouldRedirectToPendingMovements,
    shouldRedirectToPendingMovements,
    toPendingMovementsPage,
  ]);

  useEffect(() => {
    if (farmId == null) {
      return;
    }

    const populateItems = async (farmId: number) => {
      const clientNotifications = getClientNotifications();
      const pendingMovementsNotifications =
        await getPendingMovementsForFarm(farmId);

      const items = [...clientNotifications, ...pendingMovementsNotifications];
      setNotificationItems(items);
    };

    populateItems(farmId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId]);

  const handleClosePendingMovementsModal = () => {
    setShowPendingMovementsModal(false);
  };

  const countBadge = useMemo(
    () =>
      getNotificationsCount(
        notificationItems,
        pendencyOperationDate,
        viewedNotifications,
      ),
    [notificationItems, pendencyOperationDate, viewedNotifications],
  );

  return (
    <>
      <Notifier items={notificationItems} badgeCount={countBadge} />
      {showPendingMovementsModal && (
        <PendingMovementsModal onClose={handleClosePendingMovementsModal} />
      )}
    </>
  );
};

const mapStateToProps = ({ fazenda, cliente }: RootState) => ({
  farmId: fazenda.FazendaSelecionada,
  clients: cliente.Clientes,
});

export default connect(mapStateToProps, null)(Notification);
